import './services-section.scss';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

function initServicesSection() {
	const roots = document.querySelectorAll( '[data-component="services-section"]' );

	if ( ! roots.length ) {
		return;
	}

	roots.forEach( ( root ) => {
		const header = root.querySelector( '[data-services-header]' );
		const cards  = root.querySelectorAll( '.b-servicesSection__card' );
		const triggers = [];
		const mm = gsap.matchMedia();

		// Pin header only on desktop layout (matches SCSS $from: laptop / stacked below).
		if ( header ) {
			mm.add( '(min-width: 1024px)', () => {
				ScrollTrigger.create( {
					trigger: root,
					start: 'top top',
					end: 'bottom bottom',
					pin: header,
					pinSpacing: false,
				} );
			} );
		}

		// Staggered card entrance + parallax.
		cards.forEach( ( card, index ) => {
			const speed = index % 2 === 0 ? 50 : 80;

			const entranceTrigger = ScrollTrigger.create( {
				trigger: card,
				start: 'top 90%',
				toggleActions: 'play none none reverse',
				animation: gsap.from( card.children, {
					y: 100,
					opacity: 0,
					duration: 1,
					ease: 'power2.out',
					stagger: 0.1,
					force3D: true,
				} ),
			} );
			triggers.push( entranceTrigger );

			const parallaxTrigger = ScrollTrigger.create( {
				trigger: card,
				start: 'top bottom',
				end: 'bottom top',
				scrub: true,
				animation: gsap.to( card, {
					y: -speed,
					ease: 'none',
					force3D: true,
				} ),
			} );
			triggers.push( parallaxTrigger );
		} );

		initServicesBadges( root );

		root._servicesCleanup = () => {
			mm.revert();
			triggers.forEach( ( st ) => st.kill() );
			if ( root._servicesBadgeCleanup ) {
				root._servicesBadgeCleanup();
			}
		};
	} );
}

/**
 * Floating badge on service card images (same behaviour as intro section).
 *
 * @param {HTMLElement} root Services section root element.
 */
function initServicesBadges( root ) {
	const imageContainers = root.querySelectorAll( '[data-services-image]' );

	if ( ! imageContainers.length ) {
		return;
	}

	let mouseX = 0;
	let mouseY = 0;
	let activeContainer = null;
	let activeBadge = null;
	let activeXTo = null;
	let activeYTo = null;

	function onMouseMove( e ) {
		mouseX = e.clientX;
		mouseY = e.clientY;

		const el = document.elementFromPoint( mouseX, mouseY );

		if ( el ) {
			const targetContainer = el.closest( '[data-services-image]' );

			if ( targetContainer && targetContainer !== activeContainer ) {
				if ( activeContainer ) {
					activeContainer.dispatchEvent( new MouseEvent( 'mouseleave' ) );
				}
				targetContainer.dispatchEvent( new MouseEvent( 'mouseenter' ) );
			} else if ( ! targetContainer && activeContainer ) {
				activeContainer.dispatchEvent( new MouseEvent( 'mouseleave' ) );
			}
		} else if ( activeContainer ) {
			activeContainer.dispatchEvent( new MouseEvent( 'mouseleave' ) );
		}

		if ( activeContainer && activeBadge && activeXTo && activeYTo ) {
			const rect = activeContainer.getBoundingClientRect();
			activeXTo( mouseX - rect.left - activeBadge.offsetWidth / 2 );
			activeYTo( mouseY - rect.top + 20 );
		}
	}

	window.addEventListener( 'mousemove', onMouseMove );

	imageContainers.forEach( ( imageContainer ) => {
		const badge = imageContainer.querySelector( '[data-services-badge]' );

		if ( ! badge ) {
			return;
		}

		gsap.set( badge, { x: 0, y: 0, autoAlpha: 0, clipPath: 'inset(0 50% 0 50%)' } );

		const xTo = gsap.quickTo( badge, 'x', { duration: 0.6, ease: 'power3.out' } );
		const yTo = gsap.quickTo( badge, 'y', { duration: 0.6, ease: 'power3.out' } );

		imageContainer.addEventListener( 'mouseenter', () => {
			gsap.to( root.querySelectorAll( '[data-services-badge]' ), {
				autoAlpha: 0,
				clipPath: 'inset(0 50% 0 50%)',
				duration: 0.2,
				overwrite: 'auto',
			} );

			activeContainer = imageContainer;
			activeBadge = badge;
			activeXTo = xTo;
			activeYTo = yTo;

			const rect = imageContainer.getBoundingClientRect();
			gsap.set( badge, {
				x: mouseX - rect.left - badge.offsetWidth / 2,
				y: mouseY - rect.top + 20,
			} );
			gsap.to( badge, {
				autoAlpha: 1,
				clipPath: 'inset(0 0% 0 0%)',
				duration: 0.4,
				ease: 'power3.out',
				overwrite: 'auto',
			} );
		} );

		imageContainer.addEventListener( 'mouseleave', () => {
			if ( activeContainer === imageContainer ) {
				activeContainer = null;
				activeBadge = null;
				activeXTo = null;
				activeYTo = null;
			}
			gsap.to( badge, {
				autoAlpha: 0,
				clipPath: 'inset(0 50% 0 50%)',
				duration: 0.3,
				ease: 'power3.in',
				overwrite: 'auto',
			} );
		} );
	} );

	root._servicesBadgeCleanup = () => {
		window.removeEventListener( 'mousemove', onMouseMove );
	};
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initServicesSection );
} else {
	initServicesSection();
}
